// frontend/src/pages/dispatch/ShippingMethodSelection.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, CardActionArea, Typography, Button, 
  Chip, LinearProgress, Divider, Alert
} from '@mui/material';
import {
  ArrowBack, LocalShipping, Inventory, Warning,
  Business, Person, Speed
} from '@mui/icons-material';
import { manufacturingAPI } from '../../services/api';
import { toast } from 'react-toastify';
import DeliveryAddressCard from '../../components/DeliveryAddressCard';
import PackingSlipButton from '../../components/PackingSlipButton';
import usePageTitle from '../../hooks/usePageTitle';

// Shipping method configurations
const SHIPPING_METHODS = [
  {
    id: 'dx',
    name: 'DX Delivery',
    description: 'Standard DX courier service with tracking',
    icon: LocalShipping,
    color: '#1976d2',
    available: true,
    features: ['Next-day delivery', 'Full tracking', 'Proof of delivery', 'Label printing'],
    route: 'dx-shipping'
  },
  {
    id: 'manual',
    name: 'Manual / Collection',
    description: 'Customer collection or manual dispatch entry',
    icon: Person,
    color: '#388e3c',
    available: true,
    features: ['No courier needed', 'Flexible timing', 'Direct handover'],
    route: 'manual-shipping'
  },
  {
    id: 'express',
    name: 'Express Courier',
    description: 'Same-day or urgent delivery service',
    icon: Speed,
    color: '#f57c00',
    available: false,
    features: ['Same-day option', 'Priority handling', 'Time-specific delivery'],
    route: 'express-shipping'
  },
  {
    id: 'freight',
    name: 'Freight / Pallet',
    description: 'Heavy or bulk shipments via freight carrier',
    icon: Business,
    color: '#7b1fa2',
    available: false,
    features: ['Pallet delivery', 'Heavy items', 'Scheduled slots'],
    route: 'freight-shipping'
  }
];

export default function ShippingMethodSelection() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  usePageTitle(order ? `Order: ${order.order_number}` : null);

  useEffect(() => {
    loadOrderDetails();
  }, [orderId]);

  const loadOrderDetails = async () => {
    try {
      const response = await manufacturingAPI.customerOrders.get(orderId);
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to load order details:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMethod = (method) => {
    if (!method.available) {
      toast.info(`${method.name} is coming soon!`);
      return;
    }

    if (method.id === 'dx') {
      navigate(`/manufacturing/customer-orders/${orderId}/dx-shipping`);
    } else if (method.id === 'manual') {
      navigate(`/dispatch/orders/${orderId}/manual`);
    } else {
      toast.info(`${method.name} is not yet implemented`);
    }
  };

  // Calculate shippable items
  const getShippableItems = () => {
    if (!order?.lines) return [];
    // quantity_allocated already represents only the current remaining allocation
    // (shipping decrements allocations), so allocated IS the shippable amount
    return order.lines.filter(line => {
      return (line.quantity_allocated || 0) > 0;
    }).map(line => ({
      ...line,
      toShip: line.quantity_allocated || 0
    }));
  };

  const getTotalWeight = () => {
    const items = getShippableItems();
    return items.reduce((total, item) => {
      return total + (item.toShip * (item.product_weight || 0));
    }, 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Loading...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Order not found</Alert>
        <Button onClick={() => navigate('/dispatch')} sx={{ mt: 2 }}>
          Back to Dispatch
        </Button>
      </Box>
    );
  }

  const shippableItems = getShippableItems();
  const totalWeight = getTotalWeight();

  const isComplete = order.status === 'complete';
  const hasConsignments = order.dx_consignments?.filter(c => c.status !== 'deleted')?.length > 0;

  if (shippableItems.length === 0 && !isComplete && !hasConsignments) {
    return (
      <Box sx={{ p: 3 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Alert severity="warning" icon={<Warning />}>
          <Typography variant="h6">No items available to ship</Typography>
          <Typography variant="body2">
            This order has no items ready for dispatch. Please wait for production to complete.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShipping color="primary" />
          Select Shipping Method
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Order: <strong>{order.order_number}</strong> | Customer: <strong>{order.customer_name}</strong>
        </Typography>
      </Box>

      <PackingSlipButton order={order} />

      <Grid container spacing={3}>
        {/* Left Column - Shipment Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Inventory sx={{ mr: 1, verticalAlign: 'middle' }} />
                Shipment Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Items to Ship</Typography>
                <Typography variant="h4" color="primary">
                  {shippableItems.reduce((sum, item) => sum + item.toShip, 0)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Estimated Weight</Typography>
                <Typography variant="h5">
                  {totalWeight.toFixed(2)} kg
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Due Date</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatDate(order.due_date)}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>Items:</Typography>
              {shippableItems.map((item, idx) => (
                <Box 
                  key={item.id} 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    py: 0.5,
                    borderBottom: idx < shippableItems.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider'
                  }}
                >
                  <Typography variant="body2">{item.product_display}</Typography>
                  <Chip label={`x${item.toShip}`} size="small" color="success" />
                </Box>
              ))}
            </CardContent>
          </Card>

          {/* Delivery Address Card - Using Reusable Component */}
          <DeliveryAddressCard 
            order={order} 
            onUpdate={loadOrderDetails}
            showContactInfo={true}
            compact={true}
          />
        </Grid>

        {/* Right Column - Shipping Methods */}
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom>
            Choose a Shipping Method
          </Typography>
          
          <Grid container spacing={2}>
            {SHIPPING_METHODS.map((method) => {
              const IconComponent = method.icon;
              
              return (
                <Grid item xs={12} sm={6} key={method.id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      opacity: method.available ? 1 : 0.6,
                      border: '2px solid transparent',
                      '&:hover': method.available ? {
                        borderColor: method.color,
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      } : {},
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    <CardActionArea 
                      onClick={() => handleSelectMethod(method)}
                      disabled={!method.available}
                      sx={{ height: '100%', p: 2 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box 
                          sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            bgcolor: `${method.color}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <IconComponent sx={{ fontSize: 40, color: method.color }} />
                        </Box>
                        
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="h6">
                              {method.name}
                            </Typography>
                            {!method.available && (
                              <Chip label="Coming Soon" size="small" color="default" />
                            )}
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {method.description}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {method.features.map((feature, idx) => (
                              <Chip 
                                key={idx}
                                label={feature}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Box>
                      
                      {method.available && (
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'flex-end', 
                            mt: 2,
                            pt: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Button 
                            variant="contained" 
                            sx={{ bgcolor: method.color }}
                            endIcon={<LocalShipping />}
                          >
                            Select
                          </Button>
                        </Box>
                      )}
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Help Text */}
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Need help choosing?</strong>
            </Typography>
            <Typography variant="body2">
              - Use <strong>DX Delivery</strong> for standard courier shipments with tracking
            </Typography>
            <Typography variant="body2">
              - Use <strong>Manual / Collection</strong> if the customer is collecting or you're arranging your own transport
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    </Box>
  );
}